// Original, quiet feedback tones. No audio resources or context are created on import.
const TONES = {
  correct: [
    {
      frequency: 587.33, offset: 0, duration: 0.16, peak: 0.085,
    },
    {
      frequency: 880, offset: 0.105, duration: 0.23, peak: 0.075,
    },
  ],
  wrong: [
    {
      frequency: 174.61, endFrequency: 146.83, offset: 0, duration: 0.22, peak: 0.085,
    },
  ],
  milestone: [
    {
      frequency: 587.33, offset: 0, duration: 0.17, peak: 0.08,
    },
    {
      frequency: 880, offset: 0.09, duration: 0.19, peak: 0.07,
    },
    {
      frequency: 1174.66, offset: 0.18, duration: 0.28, peak: 0.06,
    },
  ],
};
const ATTACK = 0.012;
const RELEASE = 0.012;
const FLOOR = 0.0001;

// Use our envelope for interruption, including browsers without cancelAndHoldAtTime.
const envelopeValue = (voice, time) => {
  const elapsed = time - voice.start;
  if (elapsed <= 0 || elapsed >= voice.duration) return 0;
  if (elapsed < ATTACK) return voice.peak * (elapsed / ATTACK);
  if (elapsed >= voice.duration - RELEASE) {
    return FLOOR * ((voice.duration - elapsed) / RELEASE);
  }
  const progress = (elapsed - ATTACK) / (voice.duration - RELEASE - ATTACK);
  return voice.peak * ((FLOOR / voice.peak) ** progress);
};

export const createTrainerAudio = () => {
  let context = null;
  let unlocked = false;
  let pendingUnlock = null;
  let disposed = false;
  const voices = new Set();
  const disconnectedVoices = new WeakSet();

  const disconnect = (voice) => {
    if (disconnectedVoices.has(voice)) return;
    disconnectedVoices.add(voice);
    const { oscillator } = voice;
    oscillator.onended = null;
    voices.delete(voice);
    try { voice.oscillator.disconnect(); } catch (error) { /* Already disconnected. */ }
    try { voice.gain.disconnect(); } catch (error) { /* Already disconnected. */ }
  };

  const stop = () => {
    const now = context ? context.currentTime : 0;
    voices.forEach((voice) => {
      voices.delete(voice);
      try {
        // Cancel future notes too, so muting during a chime cannot leave a trailing note.
        const level = envelopeValue(voice, now);
        voice.gain.gain.cancelScheduledValues(now);
        voice.gain.gain.setValueAtTime(level, now);
        voice.gain.gain.linearRampToValueAtTime(0, now + RELEASE);
        voice.oscillator.stop(level > 0 ? now + RELEASE : now);
      } catch (error) {
        disconnect(voice);
      }
    });
  };

  const unlock = () => {
    if (disposed || typeof window === 'undefined') return Promise.resolve(false);
    try {
      if (!context || context.state === 'closed') {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return Promise.resolve(false);
        context = new AudioContextClass();
      }
      if (context.state === 'running') {
        unlocked = true;
        return Promise.resolve(true);
      }
      if (pendingUnlock) return pendingUnlock;
      const currentContext = context;
      // Call resume synchronously inside the caller's user gesture, not in a later task.
      const resumed = context.resume();
      const attempt = Promise.resolve(resumed).then(() => {
        unlocked = !disposed && context === currentContext && context.state === 'running';
        return unlocked;
      }).catch(() => {
        unlocked = false;
        return false;
      }).then((ready) => {
        if (pendingUnlock === attempt) pendingUnlock = null;
        return ready;
      });
      pendingUnlock = attempt;
      return attempt;
    } catch (error) {
      unlocked = false;
      return Promise.resolve(false);
    }
  };

  const play = (kind) => {
    const tones = TONES[kind];
    if (disposed || !unlocked || !context || context.state !== 'running' || !Array.isArray(tones)) {
      return false;
    }
    const interrupted = voices.size > 0;
    stop();
    // Leave the tiny cancellation fade room to finish before another result plays.
    const baseTime = context.currentTime + (interrupted ? RELEASE + 0.006 : 0.008);
    try {
      tones.forEach((tone) => {
        const oscillator = context.createOscillator();
        let gain;
        try {
          gain = context.createGain();
          const start = baseTime + tone.offset;
          const end = start + tone.duration;
          const voice = {
            oscillator, gain, start, duration: tone.duration, peak: tone.peak,
          };
          voices.add(voice);
          oscillator.onended = () => disconnect(voice);
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(tone.frequency, start);
          if (tone.endFrequency) {
            oscillator.frequency.exponentialRampToValueAtTime(tone.endFrequency, end);
          }
          gain.gain.setValueAtTime(0, start);
          gain.gain.linearRampToValueAtTime(tone.peak, start + ATTACK);
          gain.gain.exponentialRampToValueAtTime(FLOOR, end - RELEASE);
          gain.gain.linearRampToValueAtTime(0, end);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(start);
          oscillator.stop(end + 0.005);
        } catch (error) {
          try { oscillator.stop(); } catch (stopError) { /* Not started. */ }
          try { oscillator.disconnect(); } catch (disconnectError) { /* Already disconnected. */ }
          if (gain) gain.disconnect();
          throw error;
        }
      });
      return true;
    } catch (error) {
      stop();
      return false;
    }
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    unlocked = false;
    stop();
    if (context && context.state !== 'closed') {
      try {
        Promise.resolve(context.close()).catch(() => {});
      } catch (error) { /* Some browsers already closed the context. */ }
    }
  };

  return {
    unlock, play, stop, dispose,
  };
};

export default createTrainerAudio;
